import {
	Component,
	EventEmitter,
	Input,
	OnInit,
	Output,
	inject
} from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { Observable, combineLatest } from 'rxjs';
import { distinctUntilChanged, map } from 'rxjs/operators';

import { WebcastVideoSource, getAvailableWebcastSources } from 'rev-portal/scheduledEvents/webcast/WebcastVideoSource';

import { DialogService } from 'rev-shared/ui/dialog/Dialog.Service';
import { IMediaFeatures } from 'rev-shared/media/IMediaFeatures';
import { TranscriptionLanguagesService } from 'rev-shared/media/transcription/TranscriptionLanguages.Service';
import { WebcastViewService } from 'rev-shared/webcast/webcastView/WebcastView.Service';
import { noop } from 'rev-shared/util';

import styles from './EditWebcastVideoSettingsSidebar.Component.module.less';

import { EditWebcastModel } from './EditWebcastModel';
import { updateRtmpUrl } from './RtmpVideoSourceDeatils.Service';

@Component({
	selector: 'edit-webcast-video-settings-sidebar',
	templateUrl: './EditWebcastVideoSettingsSidebar.Component.html',
	standalone: false
})
export class EditWebcastVideoSettingsSidebarComponent implements OnInit {
	@Input() public webcast: EditWebcastModel;
	@Input() public features: IMediaFeatures;

	@Output() public sourceRestartInitiated: EventEmitter<string> = new EventEmitter();
	@Output() public switchStreamInitiated: EventEmitter<void> = new EventEmitter();

	public selectedVideoSource: { label: string, value: string };
	public selectedTranscriptionLang: { code: string, name: string };
	public selectedTranslationLangs: Array<{ code: string, name: string }>;
	public readonly styles = styles;
	public readonly WebcastVideoSource = WebcastVideoSource;
	public isRestartEligible: boolean = false;
	public availableSources: Array<{ label: string, value: string }>;
	public isRestartDisabled$: Observable<boolean>;
	public isDtmfSupportedSource: boolean = false;

	public get mainStreamUrl(): string {
		return updateRtmpUrl(this.webcast.rtmpSettings.pushUrl, this.webcast.rtmpSettings.isSecure, this.webcast.rtmpSettings.streamKey, this.webcast.videoSource);
	}

	public get backupStreamUrl(): string {
		return updateRtmpUrl(this.webcast.rtmpSettings.backupStreamUrl, this.webcast.rtmpSettings.isSecure, this.webcast.rtmpSettings.backupStreamKey, this.webcast.videoSource);
	}

	private readonly restartEligibleSources: string[] = [
		WebcastVideoSource.SIP_ADDRESS,
		WebcastVideoSource.WEBEX,
		WebcastVideoSource.PEXIP,
		WebcastVideoSource.MSTEAMS,
		WebcastVideoSource.ZOOM,
		WebcastVideoSource.RTMP,
		WebcastVideoSource.SRT
	];

	private readonly dtmfSources = [
		WebcastVideoSource.SIP_ADDRESS,
		WebcastVideoSource.PEXIP,
		WebcastVideoSource.ZOOM
	];

	private readonly DialogService = inject(DialogService);
	private readonly TranslateService = inject(TranslateService);
	private readonly LanguageService = inject(TranscriptionLanguagesService);
	public WebcastViewSvc = inject(WebcastViewService);
	private readonly translateService = inject(TranslateService);

	public ngOnInit(): void {
		this.availableSources = getAvailableWebcastSources(this.TranslateService, this.features);
		this.selectedVideoSource = this.availableSources.find(source => source.value === this.webcast.videoSource);
		if (this.webcast?.liveSubtitles?.isLiveSubtitlesEnabled) {
			const liveTranscriptionLanguages = this.LanguageService.loadLiveTranscriptionLanguages();
			const aiTranslationLanguages = this.LanguageService.loadAiServiceTranslationLanguages();
			this.selectedTranscriptionLang = liveTranscriptionLanguages.find(lang => lang.code === this.webcast.liveSubtitles?.sourceLanguage);
			this.selectedTranslationLangs = this.webcast.liveSubtitles?.translationLanguages?.map(lang =>
				aiTranslationLanguages.find(l => l.code === lang));
		}
		this.isRestartEligible = this.isWebcastRestartEligible();

		this.isRestartDisabled$ = combineLatest(
			[
				this.WebcastViewSvc.automatedWebcast$,
				this.WebcastViewSvc.videoSourceNotReady$
			]
		).pipe(
			map(([automatedWebcast, videoSourceNotReady]) => {
				return automatedWebcast || videoSourceNotReady;
			}),
			distinctUntilChanged()
		);

		this.isDtmfSupportedSource = !!this.dtmfSources.find(source => source === this.webcast.videoSource);

	}

	public onRestartSource(): void {
		this.DialogService.getDialog('RestartWebcastSource').open({
			initialState: {
				initiateRestartSource: (msg?: string) => this.initiateRestartSource(msg)
			}
		});
	}

	public onSwitchStream(): void {
		this.DialogService.openConfirmationDialog({
			title: this.translateService.instant('SwitchStream'),
			message: this.translateService.instant('SwitchStreamMsg'),
			actionText: this.translateService.instant('Ok'),
			cancelText: this.translateService.instant('Cancel'),
			themed: true
		}).result
			.then(() => this.switchStreamInitiated.emit(), noop);
	}

	private isWebcastRestartEligible(): boolean {
		return !!this.restartEligibleSources.find(source => source === this.webcast.videoSource);
	}

	private initiateRestartSource(msg?: string): void {
		this.sourceRestartInitiated.emit(msg);
	}

}
